import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

interface SearchFiltersProps {
  filters: {
    contentTypes: string[];
    difficulties: number[];
    dateRange: { from?: Date; to?: Date };
    scopeToCurrent: boolean;
  };
  onFilterChange: (filters: any) => void;
}

export function SearchFilters({ filters, onFilterChange }: SearchFiltersProps) {
  const [open, setOpen] = useState(false);

  const toggleContentType = (type: string) => {
    const updated = filters.contentTypes.includes(type)
      ? filters.contentTypes.filter((t) => t !== type)
      : [...filters.contentTypes, type];
    onFilterChange({ ...filters, contentTypes: updated });
  };

  const toggleDifficulty = (diff: number) => {
    const updated = filters.difficulties.includes(diff)
      ? filters.difficulties.filter((d) => d !== diff)
      : [...filters.difficulties, diff];
    onFilterChange({ ...filters, difficulties: updated });
  };

  const activeFilterCount =
    filters.contentTypes.length +
    filters.difficulties.length +
    (filters.scopeToCurrent ? 1 : 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-2 hover:bg-accent rounded-md">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-5">
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      
      <CollapsibleContent className="space-y-4 pt-2">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Content Type</Label>
          <div className="space-y-2">
            {["mcqs", "notes", "topics"].map((type) => (
              <div key={type} className="flex items-center space-x-2">
                <Checkbox
                  id={type}
                  checked={filters.contentTypes.includes(type)}
                  onCheckedChange={() => toggleContentType(type)}
                />
                <label
                  htmlFor={type}
                  className="text-sm capitalize cursor-pointer"
                >
                  {type}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Difficulty</Label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((diff) => (
              <Badge
                key={diff}
                variant={filters.difficulties.includes(diff) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => toggleDifficulty(diff)}
              >
                {diff}★
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="scope"
            checked={filters.scopeToCurrent}
            onCheckedChange={(checked) =>
              onFilterChange({ ...filters, scopeToCurrent: !!checked })
            }
          />
          <label htmlFor="scope" className="text-sm cursor-pointer">
            Search in current topic only
          </label>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
